import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, GetStatisticsResponse, StatisticsRequest } from './api';
import { GameWithDate } from '../types/types';
import { useNavigate } from 'react-router';

/**
 * Fetch the current (today’s) game
 */
export function useCurrentGame() {
  return useQuery({
    queryKey: ['currentGame'],
    queryFn: async () => {
      const res = await api.getCurrentGame();
      if (!res.success) {
        navigateError();
        throw new Error(res.error ?? 'Error fetching current game');
      }
      return res.data!;
    },
  });
}

/**
 * Fetch a game by date
 */
export function useGameByDate(date?: Date) {
  return useQuery({
    enabled: !!date,
    queryKey: ['gameByDate', date?.toISOString()],
    queryFn: async () => {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        navigateError();
        throw new Error('No date provided');
      }
      const res = await api.getGameByDate(date);
      if (!res.success) {
        navigateError();
        throw new Error(res.error ?? 'Error fetching game by date');
      }
      return res.data!;
    },
  });
}

/**
 * Fetch all games
 */
export function useGames(page = 1, limit = 10) {
  const navigate = useNavigate();
  return useQuery({
    queryKey: ['games', page, limit],
    queryFn: async () => {
      const res = await api.getGames(page, limit);
      if (!res.success) {
        navigate('/error');
      }
      return res.data!;
    },
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch all games (admin)
 */
export function useAllGamesAdmin() {
  return useQuery({
    queryKey: ['allGamesAdmin'],
    queryFn: async () => {
      const res = await api.getAllGamesAdmin();
      if (!res.success) throw new Error(res.error);
      return res.data!;
    },
  });
}

/**
 * Post statistics for a given date
 */
export function usePostStatistics() {
  return useMutation({
    mutationFn: async (params: {
      date: Date;
      statistics: StatisticsRequest;
    }) => {
      const res = await api.postStatisticsForDate(
        params.date,
        params.statistics,
      );
      if (!res.success) throw new Error(res.error);
      return res.data!;
    },
  });
}

/**
 * Post game (admin)
 */
export function usePostGame() {
  return useMutation({
    mutationFn: async (params: { game: Omit<GameWithDate, '_id'> }) => {
      const res = await api.uploadGameAdmin(params.game);
      if (!res.success) throw new Error(res.error);
      return res.data!;
    },
  });
}

/**
 * Delete game (admin)
 */
export function useDeleteGame() {
  return useMutation({
    mutationFn: async (params: { id: string }) => {
      const res = await api.deleteGameAdmin(params.id);
      if (!res.success) throw new Error(res.error);
      return res.data!;
    },
  });
}

/**
 * Update game (admin)
 */
export function useUpdateGame() {
  return useMutation({
    mutationFn: async (params: { game: GameWithDate }) => {
      const res = await api.updateGameAdmin(params.game);
      if (!res.success) throw new Error(res.error);
      return res.data!;
    },
  });
}

/**
 * Get statistics for a given date and player data
 */
export function useStatisticsForDate() {
  return useMutation({
    mutationFn: async (params: { date: Date; stats: StatisticsRequest }) => {
      const { date, stats } = params;
      const res = await api.getStatisticsForDate(date, stats);
      if (!res.success) throw new Error(res.error);
      return res.data as GetStatisticsResponse;
    },
  });
}

/**
 * Secure ping (for verifying login token)
 */
export function useSecurePing(enabled: boolean = false) {
  return useQuery({
    enabled,
    queryKey: ['securePing'],
    queryFn: async () => {
      const res = await api.securePing();
      if (!res.success) throw new Error(res.error);
      return res.data!;
    },
  });
}

/**
 * Login mutation
 */
export function useLogin() {
  return useMutation({
    mutationFn: async (params: { username: string; password: string }) => {
      const res = await api.login(params.username, params.password);
      if (!res.success) throw new Error(res.error);
      return res.data!;
    },
  });
}

/**
 * Logout utility (no server call needed)
 */
export function useLogout() {
  const queryClient = useQueryClient();

  function logout() {
    api.logout();
    queryClient.clear(); // Clear cached queries
  }

  return { logout };
}

function navigateError() {
  if (!window.location.pathname.startsWith('/admin')) {
    const navigate = useNavigate();
    navigate('/error');
  }
}
